// Month-landing domain helpers. The payload types themselves are generated
// from the backend schema (`MonthLandingResponse` and friends in
// `data/api-types`) — the page shape is owned by the endpoint, not restated
// here. What lives here is the non-visual logic the tab needs.
// See docs/features/month-landing-forecast/01_product_brief.md.

import type {
  MonthLandingMetricSeries,
  MonthLandingResponse,
} from "../data/api-types";
import {
  formatCalendarWeekdayDateOrDash,
  formatTimeOrDash,
  formatWeekdayDateOrDash,
} from "./formatters";

export type MonthLandingMetric = "revenue" | "contribution";

/**
 * The page's two display filters.
 *
 * `excludeBilledInAdvance` answers "what does the month look like counting only
 * work we have actually earned?"; `hideAccrualOverlay` answers "what did the
 * month look like before accounts adjusted it?".
 */
export interface MonthLandingFilters {
  excludeBilledInAdvance: boolean;
  hideAccrualOverlay: boolean;
}

export const NO_MONTH_LANDING_FILTERS: MonthLandingFilters = {
  excludeBilledInAdvance: false,
  hideAccrualOverlay: false,
};

/**
 * The landing to *show* once the filters are applied — never a second basis.
 *
 * The served `landing.central` is the one basis, computed server-side over the
 * six committed layers. A filter here subtracts a **returned layer total** from
 * it for display, so what the page shows is always the served number minus
 * something the server also sent. It never re-derives the landing from the
 * layer fields, and it never rounds, clamps or reorders.
 *
 * Result is signed: hiding a positive accrual, or keeping a negative one, can
 * legitimately put the displayed figure below zero, and a month that genuinely
 * lands negative must be shown as such.
 */
export function landingForDisplay(
  series: MonthLandingMetricSeries,
  filters: MonthLandingFilters,
): number {
  let shown = series.landing.central;
  if (filters.excludeBilledInAdvance) shown -= series.layers.billed_in_advance;
  if (filters.hideAccrualOverlay) shown -= series.layers.accrual_adjustment;
  return shown;
}

/** True when any filter is narrowing what is shown — the page must say so. */
export function isFiltered(filters: MonthLandingFilters): boolean {
  return filters.excludeBilledInAdvance || filters.hideAccrualOverlay;
}

/**
 * How the displayed figure differs from the served basis, as a caption.
 * Returns null when nothing is filtered out.
 */
export function filterCaption(
  series: MonthLandingMetricSeries,
  filters: MonthLandingFilters,
  format: (n: number) => string,
): string | null {
  const parts: string[] = [];
  if (filters.excludeBilledInAdvance) {
    parts.push(
      `${format(Math.abs(series.layers.billed_in_advance))} billed in advance`,
    );
  }
  if (filters.hideAccrualOverlay) {
    const accrual = series.layers.accrual_adjustment;
    parts.push(
      `${format(Math.abs(accrual))} of ${accrual < 0 ? "deferrals" : "accruals"}`,
    );
  }
  if (parts.length === 0) return null;
  return `Display only — ${parts.join(" and ")} removed from the ${format(
    series.landing.central,
  )} landing.`;
}

/** "£471K – £512K" style range label. */
export function formatRange(
  low: number,
  high: number,
  format: (n: number) => string,
): string {
  return `${format(low)} – ${format(high)}`;
}

/**
 * "as of Wed 12 Aug, 02:31" — the freshness label the page must carry next to
 * the as-of marker.
 *
 * Phase A has no intraday recompute: the picture is this morning's snapshot, so
 * labelling the marker "Today" alone would misrepresent bookings taken since
 * 02:30 as missing. Returns null when no snapshot has landed yet.
 */
export function formatSnapshotFreshness(
  snapshotCreatedAt: string | null | undefined,
): string | null {
  if (!snapshotCreatedAt) return null;
  const taken = new Date(snapshotCreatedAt);
  if (Number.isNaN(taken.getTime())) return null;
  const day = formatWeekdayDateOrDash(taken);
  const time = formatTimeOrDash(taken);
  return `as of ${day}, ${time}`;
}

/**
 * The snapshot days taken after the month ended — its closing tail (spec 33).
 *
 * They are not on the chart's X axis (every one of them clamps to the last
 * day), so the page offers them through their own picker rather than the
 * day-click scrubber. Empty while the month is open.
 */
export function tailSnapshotDays(view: MonthLandingResponse): string[] {
  return view.available_as_of_days.filter((d) => !d.startsWith(view.month));
}

/** "Wed 2 Sep" — a snapshot day or the tail's end, for the closing captions. */
export function formatSnapshotDay(isoDate: string): string {
  // isoDate is a calendar date, not an instant — formatCalendarWeekdayDateOrDash
  // reads its digits directly (no Date/timezone conversion, and the same
  // fixed month table every other label in the app uses).
  const formatted = formatCalendarWeekdayDateOrDash(isoDate);
  return formatted === "-" ? isoDate : formatted;
}

/**
 * The certainty ladder, most certain first.
 *
 * Fixed by spec 15 §11 and shared by the composition bar, the chart stack, both
 * legends and the unbooked-band residual below, so none of them can disagree
 * about the order or about which layers the band is measured against.
 */
export const MONTH_LANDING_STACK_ORDER = [
  "invoiced",
  "billed_in_advance",
  "whites",
  "booked_approved",
  "booked_entered",
] as const;

/**
 * The not-yet-booked band: whatever the displayed landing holds beyond the
 * named layers.
 *
 * It is a residual rather than a served field — the snapshot publishes the
 * five certainty layers and the accrual, and the band is what is left. Measured
 * against what is actually *displayed*, so a filter cannot leak into it, and
 * floored at zero so a net deferral larger than the band cannot render it
 * negative. Zero until the projection layers start arriving (`hasUnbookedBand`).
 *
 * One definition, because two consumers now read it: the composition bar draws
 * it, and the quote drill-down ties its table out against it.
 */
export function unbookedBandTotal(
  view: MonthLandingResponse,
  series: MonthLandingMetricSeries,
  filters: MonthLandingFilters,
): number {
  if (!hasUnbookedBand(view)) return 0;
  const stack = MONTH_LANDING_STACK_ORDER.filter(
    (key) => !(filters.excludeBilledInAdvance && key === "billed_in_advance"),
  ).reduce((sum, key) => sum + series.layers[key], 0);
  const accrual = filters.hideAccrualOverlay
    ? 0
    : series.layers.accrual_adjustment;
  return Math.max(0, landingForDisplay(series, filters) - stack - accrual);
}

/**
 * True once the Phase-B not-yet-booked layer is actually populated.
 *
 * Phase A ships the band hidden rather than visibly empty — an empty band
 * invites reading "nothing left to book" into what is really "not modelled
 * yet". The page asks this rather than checking a version flag, so the band
 * lights up on its own the day the projection layers start arriving.
 */
export function hasUnbookedBand(view: MonthLandingResponse): boolean {
  return Object.values(view.metrics).some((series) =>
    series.points.some((p) => p.proj_unbooked !== null),
  );
}

/**
 * The target spread across the month's **working** days, cumulatively.
 *
 * `targetPaceByDay(target, 31, [4, 5, 11, 12, ...])[day - 1]` is where the
 * month should have reached by the end of `day` to finish on target.
 *
 * Working days rather than calendar days, because the money only lands on
 * working days: a calendar-linear line climbs over every weekend while the
 * invoiced area is flat, inventing a gap each Monday that closes itself by
 * Tuesday. Spreading over working days instead holds the line flat across the
 * same days the chart already shades, so a gap on the chart is a real gap.
 *
 * Deliberately linear across those working days. Revenue is usually month-end
 * weighted, so a flat pace reads slightly pessimistic early in the month; that
 * is a known, visible bias rather than a hidden one, and shaping the curve is
 * a separate decision needing its own evidence.
 */
export function targetPaceByDay(
  target: number | null,
  daysInMonth: number,
  nonWorkingDays: readonly number[],
): (number | null)[] {
  if (target === null) return Array.from({ length: daysInMonth }, () => null);

  const offDays = new Set(nonWorkingDays);
  const totalWorkingDays = Array.from(
    { length: daysInMonth },
    (_, i) => i + 1,
  ).filter((day) => !offDays.has(day)).length;

  // A month with no working days at all cannot be paced; no line is a better
  // answer than a divide-by-zero or a flat line at the full target.
  if (totalWorkingDays === 0) {
    return Array.from({ length: daysInMonth }, () => null);
  }

  let elapsed = 0;
  return Array.from({ length: daysInMonth }, (_, i) => {
    if (!offDays.has(i + 1)) elapsed += 1;
    return (target * elapsed) / totalWorkingDays;
  });
}
